import React, { useEffect, useState } from 'react';
import { useTrans } from '../../../services/i18n';
import { SelectField, TextField } from '../../../components/common/Inputs';
import { Grid } from '@material-ui/core';
import * as Yup from 'yup';
import { useForm } from '../../../hooks/useForm';
import { useApplication } from '../../../services/application';
import { catchYupErrors, schemaToInitialState } from '../../../utils/validationUtils';
import MenuItem from '@material-ui/core/MenuItem';
import makeValidationSchema from '../../../hooks/makeValidationSchema';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useAlertContext } from '../../../contexts/AlertContext';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import PhoneNumber from '../PhoneNumber';
import { Divider } from '../../../components/common/Divider';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

const LINKEDIN_URL_FORMAT = /^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/i;

const useScheme = makeValidationSchema((trans) =>
  Yup.object().shape({
    email: Yup.string()
      .email(trans('incorrect-email'))
      .required(trans('empty-not-allowed', { field: trans('email') })),
    firstName: Yup.string().required(trans('empty-not-allowed', { field: trans('first-name') })),
    lastName: Yup.string().required(trans('empty-not-allowed', { field: trans('last-name') })),
    phone: Yup.string(),
    timezone: Yup.string(),
    formatDate: Yup.string(),
    jobTitle: Yup.string(),
    linkedInUrl: Yup.string().matches(LINKEDIN_URL_FORMAT, trans('linkedin-error-msg')),
  })
);

function getRenderValue(timezone, value) {
  const item = timezone.find((tz) => tz.abbr === value);
  return item && item.text;
}

function getDateRenderValue(dfs, value) {
  const d = dfs.find((i) => i.value === value);
  return d && d.name;
}

const useStyles = makeStyles((theme) => ({
  labelDropdowns: {
    fontFamily: 'Lexend',
    marginBottom: '8px',
    color: '#4e5978',
    fontWeight: 500,
  },
  containerCard: {
    padding: '2.125rem 1.5rem 1rem',
  },
  containerFooter: {
    padding: '1.3rem 2.3rem 1.87rem 2.3rem',
    marginTop: 'unset',
    justifyContent: 'flex-end',
  },
  saveButton: {
    minWidth: 120,
  },
  progress: {
    marginRight: theme.spacing(1),
  },
  phoneField: {
    position: 'relative',
    background: '#f1f3f9',
    borderRadius: '4px',
    height: 56,
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px',
    paddingRight: '92px',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  phoneInput: {
    width: '100%',
    minWidth: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    '& .PhoneInputCountry': {
      marginRight: '8px',
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
    },
    '& .PhoneInputCountryIcon': {
      boxShadow: 'none',
    },
    '& .PhoneInputCountrySelectArrow': {
      opacity: 0.6,
      marginLeft: '2px',
    },
    '& .PhoneInputInput': {
      width: '100%',
      minWidth: 0,
      height: '100%',
      border: 'none',
      outline: 'none',
      background: 'transparent',
      boxShadow: 'none',
      padding: 0,
      fontFamily: 'Lexend',
      fontSize: '16px',
      lineHeight: '56px',
      color: '#4e5978',
    },
    '& .PhoneInputInput:disabled': {
      opacity: 1,
      background: 'transparent',
      color: '#4e5978',
      WebkitTextFillColor: '#4e5978',
      cursor: 'default',
    },
    '& .PhoneInputInput::placeholder': {
      color: '#98a1b2',
    },
  },
  phoneAction: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
  },
}));

const formatDates = [
  {
    name: 'DD/MM/YYYY',
    value: 'DD/MM/YYYY',
  },
  {
    name: 'MM/DD/YYYY',
    value: 'MM/DD/YYYY',
  },
];

export default function PersonalInformation({ user, onUpdate }) {
  const { trans } = useTrans();
  const application = useApplication();
  const schema = useScheme();
  const [pending, setPending] = useState(false);
  const [timezone, setTimezone] = React.useState([]);
  const styles = useStyles();
  const { successAlert, errorAlert } = useAlertContext();

  useEffect(() => {
    application.call('utilities.timezones').then((val) => setTimezone(val));
  }, [application]);

  const onSubmit = React.useCallback(
    async (values, { setStatus, setErrors }) => {
      try {
        setPending(true);

        const payload = {
          firstName: user?.name || '',
          lastName: user?.familyName || '',
          email: user?.email || '',
          timezone: values.timezone,
          formatDate: values.formatDate,
          phone: values.phone,
          jobTitle: values.jobTitle,
          linkedInUrl: values.linkedInUrl,
        };

        const result = await onUpdate(payload);

        successAlert(trans('personal-info-updated-successfully'));

        localStorage.setItem('authFormatDate', result.formatDate ? result.formatDate : values.formatDate || 'MM/DD/YYYY');
        localStorage.setItem('authTimezone', result.authTimezone || result.timezone || values.timezone);
      } catch (e) {
        errorAlert(e.message);
        catchYupErrors(e, setErrors, (err) => setStatus({ message: err.message }));
      } finally {
        setPending(false);
      }
    },
    [errorAlert, onUpdate, successAlert, trans, user]
  );

  const { setValues, getFieldProps, handleSubmit, values } = useForm(schema, schemaToInitialState(schema), onSubmit, {
    validateOnChange: false,
    validateOnBlur: false,
  });

  useEffect(() => {
    setValues({
      firstName: user?.name || '',
      lastName: user?.familyName || '',
      email: user?.email || '',
      timezone: user?.timezone || '',
      formatDate: user?.formatDate || 'MM/DD/YYYY',
      phone: user?.phone || '',
      jobTitle: user?.jobTitle || '',
      linkedInUrl: user?.linkedInUrl || '',
    });
  }, [user, setValues]);

  return (
    <React.Fragment>
      <Grid container spacing={3} className={styles.containerCard}>
        <Grid item sm={6} xs={12}>
          <TextField
            type="text"
            label={trans('first-name')}
            {...getFieldProps('firstName')}
            disabled
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <TextField
            type="text"
            label={trans('last-name')}
            {...getFieldProps('lastName')}
            disabled
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <TextField
            type="text"
            label={trans('email')}
            {...getFieldProps('email')}
            disabled
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <div className={styles.labelDropdowns}>{trans('phone')}</div>
          <div className={styles.phoneField}>
            <PhoneInput
              international
              {...getFieldProps('phone')}
              className={styles.phoneInput}
              disabled
            />

            <div className={styles.phoneAction}>
              <PhoneNumber auth={user} />
            </div>
          </div>
        </Grid>

        <Grid item sm={12} xs={12}>
          <TextField
            type="text"
            label={trans('job-title')}
            {...getFieldProps('jobTitle')}
            placeholder={trans('optional')}
          />
        </Grid>

        <Grid item sm={12} xs={12}>
          <TextField
            type="text"
            label={trans('linkedin-profile')}
            {...getFieldProps('linkedInUrl')}
            placeholder={trans('optional')}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <div className={styles.labelDropdowns}>{trans('timezone')}</div>
          <SelectField
            placeholder={trans('select-timezone')}
            type="select"
            getRenderValue={getRenderValue.bind(null, timezone)}
            {...getFieldProps('timezone')}
          >
            {timezone &&
              timezone.map((tz, index) => (
                <MenuItem key={`tz_${index}`} value={tz.abbr}>
                  {tz.text}
                </MenuItem>
              ))}
          </SelectField>
        </Grid>

        <Grid item sm={6} xs={12}>
          <div className={styles.labelDropdowns}>Date Format</div>
          <SelectField
            type="select"
            getRenderValue={getDateRenderValue.bind(null, formatDates, values.formatDate)}
            {...getFieldProps('formatDate')}
          >
            {formatDates &&
              formatDates.map((fd, index) => (
                <MenuItem key={`fd_${index}`} value={fd.value}>
                  {fd.name}
                </MenuItem>
              ))}
          </SelectField>
        </Grid>
      </Grid>

      <Divider light spacing={1} />

      <Grid container className={styles.containerFooter}>
        <Button
          color="primary"
          variant="contained"
          className={styles.saveButton}
          disabled={pending}
          onClick={handleSubmit}
        >
          {pending && <CircularProgress size={16} className={styles.progress} />}
          {trans('save')}
        </Button>
      </Grid>
    </React.Fragment>
  );
}